import Vue from 'vue'
import intlTelInput from 'intl-tel-input/intlTelInputWithUtils'

Vue.component('phone-input', {
  props: {
    name: {
      type: String,
      default: 'customer[phone]'
    },
    placeholder: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    initialCountry: {
      type: String,
      default: 'auto'
    },
    initialValue: {
      type: [String, Function],
      default: ''
    },
    preferredCountries: {
      type: Array,
      default () {
        return ['gb', 'us', 'ca', 'au', 'de', 'fr', 'it', 'es']
      }
    },
    required: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: 'Please enter a valid phone number'
    },
    requiredError: {
      type: String,
      default: 'Phone is required'
    },
    separateDialCode: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      iti: null,
      inputValue: '',
      isValid: true,
      dirty: false,
      errorMessage: ''
    }
  },

  computed: {
    showError () {
      return this.dirty && !this.isValid
    },
    resolvedError () {
      return this.resolveMessage(this.error)
    },
    resolvedRequiredError () {
      return this.resolveMessage(this.requiredError)
    }
  },

  watch: {
    initialCountry (country) {
      this.setCountry(country)
    },
    initialValue (value) {
      this.setValue(value)
    }
  },

  mounted () {
    const input = this.$refs.input
    if (!input) return

    this.iti = intlTelInput(input, {
      initialCountry: this.initialCountry,
      initialCountryLookup: this.initialCountry === 'auto' ? this.lookupInitialCountry : null,
      countryOrder: this.preferredCountries,
      separateDialCode: this.separateDialCode,
      strictMode: true,
      formatAsYouType: true,
      countrySearch: true
    })

    this.setValue(this.initialValue)

    input.addEventListener('countrychange', this.onCountryChange)
    input.addEventListener('blur', this.onBlur)
    input.addEventListener('input', this.onInput)
  },

  beforeDestroy () {
    const input = this.$refs.input
    if (input) {
      input.removeEventListener('countrychange', this.onCountryChange)
      input.removeEventListener('blur', this.onBlur)
      input.removeEventListener('input', this.onInput)
    }
    if (this.iti) {
      this.iti.destroy()
      this.iti = null
    }
  },

  methods: {
    setCountry (country) {
      if (!this.iti || !country || country === 'auto') return

      this.iti.setCountry(country.toLowerCase())
    },

    setValue (value) {
      const input = this.$refs.input

      if (!input) return

      const normalizedValue = typeof value === 'function'
        ? value(this.name)
        : value || ''

      if (this.iti && normalizedValue) {
        this.iti.setNumber(normalizedValue)
      } else {
        input.value = normalizedValue
      }

      this.inputValue = input.value
    },

    lookupInitialCountry () {
      const cacheKey = 'iti_country'
      const cached = window.sessionStorage && window.sessionStorage.getItem(cacheKey)
      if (cached) {
        return Promise.resolve(cached)
      }

      return fetch('https://ipapi.co/json/')
        .then(res => res.ok ? res.json() : Promise.reject(new Error('lookup failed')))
        .then(data => {
          const code = (data.country_code || 'gb').toLowerCase()
          try {
            window.sessionStorage && window.sessionStorage.setItem(cacheKey, code)
          } catch (err) { /* ignore quota errors */ }
          return code
        })
        .catch(() => {
          return 'gb'
        })
    },

    onCountryChange () {
      if (this.dirty) this.validateInput()
      this.$emit('change', { value: this.getE164(), name: this.name })
    },

    onBlur () {
      this.dirty = true
      this.validateInput()
    },

    onInput () {
      this.inputValue = this.$refs.input.value
      if (this.dirty) {
        this.validateInput()
      }
      this.$emit('change', { value: this.inputValue, name: this.name })
    },

    validateInput () {
      if (!this.iti) {
        this.isValid = true
        return { valid: true, message: false }
      }

      const raw = this.$refs.input.value.trim()

      if (!raw) {
        this.isValid = !this.required
        this.errorMessage = this.required ? this.resolvedRequiredError : ''
        return {
          valid: this.isValid,
          message: this.required ? this.errorMessage : false
        }
      }

      const valid = this.iti.isValidNumber()
      this.isValid = valid
      this.errorMessage = valid ? '' : this.resolvedError

      return {
        valid,
        message: valid ? false : this.errorMessage
      }
    },

    getE164 () {
      if (!this.iti) return this.$refs.input ? this.$refs.input.value : ''
      const number = this.iti.getNumber()
      return number || ''
    },

    getSelectedCountryData () {
      if (!this.iti) return { iso2: '', dialCode: '' }
      return this.iti.getSelectedCountryData()
    },

    normalizeBeforeSubmit () {
      const result = this.validateInput()
      if (!result.valid) {
        this.dirty = true
        return false
      }
      if (this.iti && this.$refs.input.value.trim()) {
        const e164 = this.iti.getNumber()
        if (e164) this.$refs.input.value = e164
      }
      return true
    },

    resolveMessage (message) {
      if (!message || typeof message !== 'string') return message
      if (!message.includes('.')) return message

      const translated = this.$t(message)
      return translated === message ? message : translated
    }
  },

  template: `
    <div class="phone-input" :class="{ 'is-error': showError }">
      <input
        ref="input"
        type="tel"
        :name="name"
        :placeholder="placeholder"
        :aria-label="label || placeholder"
        autocomplete="tel">
      <transition name="fade">
        <p v-if="showError" class="phone-input__error">
          <span>{{ errorMessage || resolvedError }}</span>
        </p>
      </transition>
    </div>
  `
})
